import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { AlertCircle, CheckCircle } from 'lucide-react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

const HeaderCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 2rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  margin-bottom: 2rem;
`;

const Title = styled.h2`
  font-size: 1.5rem;
  font-weight: 600;
  color: #1e293b;
  margin: 0 0 0.5rem 0;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const Subtitle = styled.p`
  color: #64748b;
  font-size: 0.9375rem;
  margin: 0;
`;

const OrdersList = styled.div`
  display: grid;
  gap: 1rem;
`;

const OrderCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
  transition: all 0.2s;
  
  &:hover {
    border-color: #667eea;
    box-shadow: 0 4px 12px rgba(102, 126, 234, 0.1);
  }
`;

const OrderHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: start;
  margin-bottom: 1rem;
`;

const OrderTitle = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1e293b;
`;

const OrderInfo = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 0.75rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
`;

const InfoItem = styled.div`
  display: flex;
  gap: 0.5rem;
  
  span:first-child {
    color: #64748b;
    font-weight: 500;
  }
  
  span:last-child {
    color: #1e293b;
  }
`;

const ReasonBox = styled.div`
  background: #fef3c7;
  border-left: 4px solid #f59e0b;
  padding: 0.75rem 1rem;
  border-radius: 6px;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: #92400e;
`;

const CompleteButton = styled.button`
  padding: 0.75rem 1.5rem;
  background: #059669;
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  width: 100%;
  justify-content: center;
  
  &:hover {
    background: #047857;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(5, 150, 105, 0.3);
  }
`;

const NoOrders = styled.div`
  background: white;
  border-radius: 12px;
  padding: 3rem;
  text-align: center;
  color: #64748b;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const LoadingState = styled.div`
  background: white;
  border-radius: 12px;
  padding: 3rem;
  text-align: center;
  color: #64748b;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
`;

const IncompleteOrdersPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [incompleteOrders, setIncompleteOrders] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchIncompleteOrders();
  }, []);

  const fetchIncompleteOrders = async () => {
    const userData = localStorage.getItem('user');
    if (!userData) return;

    try {
      const user = JSON.parse(userData);
      const userId = user.id || user._id;
      if (!userId) return;

      setLoading(true);
      const token = localStorage.getItem('token');
      // Don't include orders with reasons for the list view
      const response = await axios.get(`/api/orders/incomplete/${userId}`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (response.data.incompleteOrders) {
        setIncompleteOrders(response.data.incompleteOrders);
      }
    } catch (error) {
      console.error('Error fetching incomplete orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteOrder = (incompleteOrder) => {
    try {
      const visitor = incompleteOrder.visitor;
      const prescription = incompleteOrder.prescription;
      
      const visitorData = {
        gender: visitor?.gender || 'male',
        ageGroup: visitor?.ageGroup || 'adult',
        visitDate: visitor?.visitDate || new Date().toISOString()
      };
      
      sessionStorage.setItem('visitorData', JSON.stringify(visitorData));
      
      if (visitor?._id) {
        sessionStorage.setItem('visitorId', visitor._id);
      }
      
      if (prescription) {
        sessionStorage.setItem('existingPrescription', JSON.stringify(prescription));
        if (prescription._id) {
          sessionStorage.setItem('prescriptionId', prescription._id);
        }
      }
      
      navigate('/prescription');
    } catch (error) {
      console.error('Error completing order:', error);
      alert(t('incomplete.loadError'));
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (e) {
      return 'N/A';
    }
  };

  if (loading) {
    return (
      <PageContainer>
        <LoadingState>{t('incomplete.loading')}</LoadingState>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <HeaderCard>
        <Title>
          <AlertCircle size={24} />
          {t('incomplete.title')}
        </Title>
        <Subtitle>
          {t('incomplete.found', { count: incompleteOrders.length })}
        </Subtitle>
      </HeaderCard>

      {incompleteOrders.length > 0 ? (
        <OrdersList>
          {incompleteOrders.map((order, index) => (
            <OrderCard key={index}>
              <OrderHeader>
                <OrderTitle>
                  {t('incomplete.orderNum', { num: index + 1 })} - {order.prescription?.patientName || t('incomplete.visitorOnly')}
                </OrderTitle>
              </OrderHeader>
              
              <OrderInfo>
                {order.prescription?.patientName && (
                  <InfoItem>
                    <span>{t('incomplete.patientName')}</span>
                    <span>{order.prescription.patientName}</span>
                  </InfoItem>
                )}
                {order.prescription?.patientAge && (
                  <InfoItem>
                    <span>{t('incomplete.age')}</span>
                    <span>{t('incomplete.years', { age: order.prescription.patientAge })}</span>
                  </InfoItem>
                )}
                {order.prescription?.phoneNumber && (
                  <InfoItem>
                    <span>{t('incomplete.phone')}</span>
                    <span>{order.prescription.phoneNumber}</span>
                  </InfoItem>
                )}
                {order.visitor?.visitDate && (
                  <InfoItem>
                    <span>{t('incomplete.visitDate')}</span>
                    <span>{formatDate(order.visitor.visitDate)}</span>
                  </InfoItem>
                )}
                {order.salesPerson && (
                  <InfoItem>
                    <span>{t('incomplete.employee')}</span>
                    <span>
                      {order.salesPerson.firstName || ''} {order.salesPerson.lastName || ''}
                      {order.salesPerson.employeeId && ` (${order.salesPerson.employeeId})`}
                    </span>
                  </InfoItem>
                )}
                {order.store && (
                  <InfoItem>
                    <span>{t('incomplete.store')}</span>
                    <span>{order.store.name || order.store.storeCode || 'N/A'}</span>
                  </InfoItem>
                )}
              </OrderInfo>

              {order.reason && (
                <ReasonBox>
                  <strong>{t('incomplete.reason')}</strong> {order.reason}
                </ReasonBox>
              )}

              <CompleteButton onClick={() => handleCompleteOrder(order)}>
                <CheckCircle size={18} />
                {t('incomplete.completeOrder')}
              </CompleteButton>
            </OrderCard>
          ))}
        </OrdersList>
      ) : (
        <NoOrders>
          <AlertCircle size={48} style={{ margin: '0 auto 1rem', color: '#cbd5e1' }} />
          <p>{t('incomplete.none')}</p>
          <p style={{ fontSize: '0.875rem', marginTop: '0.5rem' }}>
            {t('incomplete.noneSub')}
          </p>
        </NoOrders>
      )}
    </PageContainer>
  );
};

export default IncompleteOrdersPage;

